import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../dashboard/components/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { saveRequisition } from '../../services/requisitionService';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';



const MedicalRequisitionForm = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Doctor's Credentials State (pre-populated from profile)
  const [doctorInfo, setDoctorInfo] = useState({
    registrationNumber: profile?.license_number || '',
    doctorName: profile?.full_name || '',
    specialty: profile?.specialization || '',
    clinicName: profile?.institution_name || '',
    clinicAddress: '',
    phoneNumber: '',
    email: user?.email || '',
    signature: ''
  });

  // Patient Information State
  const [patientInfo, setPatientInfo] = useState({
    patientName: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    idNumber: '',
    address: '',
    city: '',
    postalCode: '',
    phoneNumber: '',
    email: '',
    emergencyContact: '',
    emergencyPhone: ''
  });

  // Clinical Information State
  const [clinicalInfo, setClinicalInfo] = useState({
    clinicalHistory: '',
    symptoms: '',
    relevantFindings: '',
    previousImaging: '',
    medications: '',
    allergies: '',
    contraindications: '',
    clinicalQuestion: ''
  });

  // Imaging Request State
  const [imagingRequest, setImagingRequest] = useState({
    modality: '',
    bodyRegion: '',
    contrast: 'no',
    urgency: 'routine',
    preferredDate: '',
    irefer_code: '',
    additionalNotes: ''
  });

  const [consentChecked, setConsentChecked] = useState(false);

  const handleDoctorChange = (e) => {
    setDoctorInfo({ ...doctorInfo, [e?.target?.name]: e?.target?.value });
  };

  const handlePatientChange = (e) => {
    const { name, value } = e?.target;
    setPatientInfo({ ...patientInfo, [name]: value });

    // Auto-calculate age from date of birth
    if (name === 'dateOfBirth' && value) {
      const dob = new Date(value);
      const today = new Date();
      let age = today?.getFullYear() - dob?.getFullYear();
      const monthDiff = today?.getMonth() - dob?.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < dob?.getDate())) {
        age--;
      }
      setPatientInfo(prev => ({ ...prev, age: age?.toString() }));
    }
  };

  const handleClinicalChange = (e) => {
    setClinicalInfo({ ...clinicalInfo, [e?.target?.name]: e?.target?.value });
  };

  const handleImagingChange = (e) => {
    setImagingRequest({ ...imagingRequest, [e?.target?.name]: e?.target?.value });
  };

  const validateForm = () => {
    // Required doctor fields
    if (!doctorInfo?.registrationNumber || !doctorInfo?.doctorName || !doctorInfo?.phoneNumber || !doctorInfo?.signature) {
      setSubmitError('Please complete all required doctor information fields');
      return false;
    }

    // Required patient fields
    if (!patientInfo?.patientName || !patientInfo?.dateOfBirth || !patientInfo?.gender || !patientInfo?.idNumber || !patientInfo?.phoneNumber) {
      setSubmitError('Please complete all required patient information fields');
      return false;
    }

    // Required clinical fields
    if (!clinicalInfo?.clinicalHistory || !clinicalInfo?.allergies || !clinicalInfo?.clinicalQuestion) {
      setSubmitError('Please complete all required clinical information fields');
      return false;
    }

    // Required imaging fields
    if (!imagingRequest?.modality || !imagingRequest?.bodyRegion) {
      setSubmitError('Please complete all required imaging request fields');
      return false;
    }

    // Consent
    if (!consentChecked) {
      setSubmitError('Please confirm the consent statement');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const requisitionData = {
        doctorInfo,
        patientInfo,
        clinicalInfo,
        imagingRequest
      };

      const savedRequisition = await saveRequisition(user?.id, requisitionData);

      setSubmitSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Error submitting requisition:', error);
      setSubmitError(error?.message || 'Failed to submit requisition. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation userRole={profile?.role || 'clinician'} />

      <div className="pt-header">
        <div className="max-w-7xl mx-auto container-padding py-6 sm:py-8 md:py-12">
          {submitSuccess && (
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="CheckCircle" size={24} color="var(--color-success)" />
                <div>
                  <p className="text-success font-semibold">Requisition Submitted Successfully!</p>
                  <p className="text-sm text-muted-foreground">Redirecting to dashboard...</p>
                </div>
              </div>
            </div>
          )}

          {submitError && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg">
              <div className="flex items-center gap-3">
                <Icon name="AlertCircle" size={24} color="var(--color-destructive)" />
                <p className="text-destructive">{submitError}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="border-b-2 border-primary pb-4 mb-6">
              <h1 className="text-3xl font-bold text-primary">MEDICAL IMAGING REQUISITION</h1>
              <p className="text-muted-foreground mt-2">mydocrefer.org - Evidence-Based Imaging</p>
            </div>

            {/* SECTION 1: DOCTOR'S CREDENTIALS */}
            <div className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h2 className="text-2xl font-semibold text-blue-900 dark:text-blue-100 mb-4 flex items-center gap-2">
                <Icon name="UserCheck" size={24} />
                REFERRING PHYSICIAN INFORMATION
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Medical Registration Number <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="registrationNumber"
                    value={doctorInfo?.registrationNumber}
                    onChange={handleDoctorChange}
                    placeholder="e.g., MC123456"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Doctor's Full Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="doctorName"
                    value={doctorInfo?.doctorName}
                    onChange={handleDoctorChange}
                    placeholder="Dr. [Full Name]"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Specialty
                  </label>
                  <input
                    type="text"
                    name="specialty"
                    value={doctorInfo?.specialty}
                    onChange={handleDoctorChange}
                    placeholder="e.g., General Practice, Oncology"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Clinic/Hospital Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="clinicName"
                    value={doctorInfo?.clinicName}
                    onChange={handleDoctorChange}
                    placeholder="Clinic or Hospital Name"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Clinic Address <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="clinicAddress"
                    value={doctorInfo?.clinicAddress}
                    onChange={handleDoctorChange}
                    placeholder="Full Address"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Contact Number <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={doctorInfo?.phoneNumber}
                    onChange={handleDoctorChange}
                    placeholder="+1234567890"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Email Address <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={doctorInfo?.email}
                    onChange={handleDoctorChange}
                    placeholder="doctor@clinic.com"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 2: PATIENT INFORMATION */}
            <div className="mb-8 p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <h2 className="text-2xl font-semibold text-green-900 dark:text-green-100 mb-4 flex items-center gap-2">
                <Icon name="User" size={24} />
                PATIENT INFORMATION
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Patient's Full Name <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="patientName"
                    value={patientInfo?.patientName}
                    onChange={handlePatientChange}
                    placeholder="Full Legal Name"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Date of Birth <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={patientInfo?.dateOfBirth}
                    onChange={handlePatientChange}
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Age
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={patientInfo?.age}
                    readOnly
                    placeholder="Auto-calculated"
                    className="w-full px-4 py-2 border border-input rounded-md bg-muted text-muted-foreground"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Gender <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="gender"
                    value={patientInfo?.gender}
                    onChange={handlePatientChange}
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    ID/Passport Number <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="idNumber"
                    value={patientInfo?.idNumber}
                    onChange={handlePatientChange}
                    placeholder="National ID or Passport"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Phone Number <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={patientInfo?.phoneNumber}
                    onChange={handlePatientChange}
                    placeholder="+1234567890"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Home Address <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={patientInfo?.address}
                    onChange={handlePatientChange}
                    placeholder="Street Address"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={patientInfo?.city}
                    onChange={handlePatientChange}
                    placeholder="City"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={patientInfo?.postalCode}
                    onChange={handlePatientChange}
                    placeholder="Postal Code"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={patientInfo?.email}
                    onChange={handlePatientChange}
                    placeholder="patient@email.com"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Emergency Contact Name
                  </label>
                  <input
                    type="text"
                    name="emergencyContact"
                    value={patientInfo?.emergencyContact}
                    onChange={handlePatientChange}
                    placeholder="Emergency Contact"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Emergency Contact Phone
                  </label>
                  <input
                    type="tel"
                    name="emergencyPhone"
                    value={patientInfo?.emergencyPhone}
                    onChange={handlePatientChange}
                    placeholder="+1234567890"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 3: CLINICAL INFORMATION */}
            <div className="mb-8 p-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <h2 className="text-2xl font-semibold text-yellow-900 dark:text-yellow-100 mb-4 flex items-center gap-2">
                <Icon name="FileText" size={24} />
                CLINICAL INFORMATION & PATIENT HISTORY
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Clinical History / Reason for Examination <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    name="clinicalHistory"
                    value={clinicalInfo?.clinicalHistory}
                    onChange={handleClinicalChange}
                    placeholder="Provide detailed clinical history, presenting complaints, duration of symptoms..."
                    required
                    rows="4"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Current Symptoms
                  </label>
                  <textarea
                    name="symptoms"
                    value={clinicalInfo?.symptoms}
                    onChange={handleClinicalChange}
                    placeholder="List current symptoms, their onset, severity, and progression..."
                    rows="3"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Relevant Clinical Findings
                  </label>
                  <textarea
                    name="relevantFindings"
                    value={clinicalInfo?.relevantFindings}
                    onChange={handleClinicalChange}
                    placeholder="Physical examination findings, laboratory results, etc..."
                    rows="3"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Previous Imaging / Investigations
                  </label>
                  <textarea
                    name="previousImaging"
                    value={clinicalInfo?.previousImaging}
                    onChange={handleClinicalChange}
                    placeholder="List any previous relevant imaging studies, dates, and facilities..."
                    rows="2"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Current Medications
                  </label>
                  <textarea
                    name="medications"
                    value={clinicalInfo?.medications}
                    onChange={handleClinicalChange}
                    placeholder="List all current medications and dosages..."
                    rows="2"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Allergies (especially contrast media) <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    name="allergies"
                    value={clinicalInfo?.allergies}
                    onChange={handleClinicalChange}
                    placeholder="List any known allergies, especially to contrast agents, medications, or latex. Write 'NONE' if no allergies."
                    required
                    rows="2"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Contraindications / Special Considerations
                  </label>
                  <textarea
                    name="contraindications"
                    value={clinicalInfo?.contraindications}
                    onChange={handleClinicalChange}
                    placeholder="Pregnancy, renal impairment, pacemaker, metallic implants, claustrophobia, etc..."
                    rows="2"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-destructive mt-1">
                    ⚠️ CRITICAL: Indicate if patient is pregnant, has renal impairment, pacemaker, or metallic implants
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Clinical Question / What information is needed? <span className="text-destructive">*</span>
                  </label>
                  <textarea
                    name="clinicalQuestion"
                    value={clinicalInfo?.clinicalQuestion}
                    onChange={handleClinicalChange}
                    placeholder="What specific clinical question should this imaging answer? e.g., 'Exclude pulmonary embolism', 'Characterize renal mass', etc..."
                    required
                    rows="2"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>
              </div>
            </div>

            {/* SECTION 4: IMAGING REQUEST */}
            <div className="mb-8 p-6 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <h2 className="text-2xl font-semibold text-purple-900 dark:text-purple-100 mb-4 flex items-center gap-2">
                <Icon name="Activity" size={24} />
                IMAGING REQUEST
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Imaging Modality Requested <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="modality"
                    value={imagingRequest?.modality}
                    onChange={handleImagingChange}
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select Modality</option>
                    <option value="xray">X-Ray</option>
                    <option value="ct">CT Scan</option>
                    <option value="mri">MRI</option>
                    <option value="ultrasound">Ultrasound</option>
                    <option value="mammography">Mammography</option>
                    <option value="pet-ct">PET-CT</option>
                    <option value="dexa">DEXA Scan</option>
                    <option value="fluoroscopy">Fluoroscopy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Body Region / Anatomical Area <span className="text-destructive">*</span>
                  </label>
                  <input
                    type="text"
                    name="bodyRegion"
                    value={imagingRequest?.bodyRegion}
                    onChange={handleImagingChange}
                    placeholder="e.g., Chest, Abdomen, Brain, Right Knee"
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Contrast Required?
                  </label>
                  <select
                    name="contrast"
                    value={imagingRequest?.contrast}
                    onChange={handleImagingChange}
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="no">No Contrast</option>
                    <option value="yes">With Contrast</option>
                    <option value="both">With and Without Contrast</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Urgency Level <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="urgency"
                    value={imagingRequest?.urgency}
                    onChange={handleImagingChange}
                    required
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="routine">Routine (2-4 weeks)</option>
                    <option value="urgent">Urgent (24-48 hours)</option>
                    <option value="emergency">Emergency (Immediate)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Preferred Appointment Date
                  </label>
                  <input
                    type="date"
                    name="preferredDate"
                    value={imagingRequest?.preferredDate}
                    onChange={handleImagingChange}
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    iRefer Reference Code (if known)
                  </label>
                  <input
                    type="text"
                    name="irefer_code"
                    value={imagingRequest?.irefer_code}
                    onChange={handleImagingChange}
                    placeholder="e.g., B06, CA17, GU03"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Additional Notes / Special Instructions
                  </label>
                  <textarea
                    name="additionalNotes"
                    value={imagingRequest?.additionalNotes}
                    onChange={handleImagingChange}
                    placeholder="Any additional information for the radiologist or imaging facility..."
                    rows="3"
                    className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  />
                </div>
              </div>
            </div>

            {/* SUBMISSION SECTION */}
            <div className="mb-6 p-6 bg-card rounded-lg border border-border">
              <div className="flex items-start mb-4">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e?.target?.checked)}
                  required
                  className="mt-1 h-4 w-4 text-primary"
                />
                <label htmlFor="consent" className="ml-2 text-sm text-foreground">
                  I confirm that the information provided is accurate and complete. I have obtained patient consent for this imaging examination and understand the clinical indication justifies the radiation exposure (if applicable). <span className="text-destructive">*</span>
                </label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-1">
                  Digital Signature <span className="text-destructive">*</span>
                </label>
                <input
                  type="text"
                  name="signature"
                  value={doctorInfo?.signature}
                  onChange={handleDoctorChange}
                  placeholder="Type your full name as digital signature"
                  required
                  className="w-full px-4 py-2 border border-input rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Date: {new Date()?.toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="ghost"
                onClick={handlePrint}
                iconName="Printer"
                iconPosition="left"
              >
                Print Requisition
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                iconName={isSubmitting ? "Loader" : "Send"}
                iconPosition="left"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Requisition'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MedicalRequisitionForm;